import {
  DealType,
  IAgentOfficeBulletin,
  IAgentOfficeDealEnriched,
  PoiId,
} from 'utils/entities';
import { values } from 'lodash';
import { createSelector } from 'reselect';
import { makeByIdsDictionarySelector } from 'store/state/domainData/selectors/common';
import { State } from 'store/state';
import { IAgentOfficeFeatureProps } from 'components/map/types';
import { makeFeature } from 'components/map/selectors';
import GeoJSON from 'geojson';
import { officePageBulletinSelector, officePageDataSelector } from 'store/state/domainData/selectors/officeById';
import { agentPageBulletinSelector } from 'store/state/domainData/selectors/agentById';
import { routeSelector } from 'store/state/selectors/router';
import { Route } from 'config/routes';
import { LegendStatus, LegendType } from './Legend';

const makeAgentOfficeListingProperties = (office: IAgentOfficeBulletin | IAgentOfficeDealEnriched, isActive: boolean): IAgentOfficeFeatureProps => ({
  id: office.id,
  featureType: 'agentOfficeBulletinTarget',
  isActive,
  dealType: office.dealType,
  poiType: office.type,
});


export const officeBulletinsDictionarySelector = makeByIdsDictionarySelector(officePageBulletinSelector);
export const agentBulletinsDictionarySelector = makeByIdsDictionarySelector(agentPageBulletinSelector);

export const agentOfficeBulletinsDictionarySelector = createSelector([
  routeSelector,
  officePageDataSelector,
  officeBulletinsDictionarySelector,
  agentBulletinsDictionarySelector,
], (route, officePage, officeBulletins, agentBulletins) => {
  if (route.name === Route.OfficePage) {
    return officeBulletins;
  }
  else if (route.name === Route.AgentPage) {
    return officePage ? (officePage.allOfficeBulletinsInAgentPage ? officeBulletins : agentBulletins) : [];
  }
  else {
    return [];
  }
});

export const agentOfficeMapLegentInitialStateSelector = createSelector([ routeSelector, officePageBulletinSelector, agentPageBulletinSelector, officePageDataSelector ], (route, office, agent, officeData) => {
  let list;
  if (route.name === Route.OfficePage || officeData.allOfficeBulletinsInAgentPage) {
    list = office;
  }
  else if (route.name === Route.AgentPage) {
    list = agent;
  }
  const isBuyEnable = Boolean(list.filter(bulletin => bulletin.dealType === DealType.Buy).length);
  const isRentEnable = Boolean(list.filter(bulletin => bulletin.dealType === DealType.Rent).length);
  const isSoldEnable = Boolean(list.filter(bulletin => bulletin.dealType === 'sold').length);
  return {
    [LegendType.Sale]: isBuyEnable ? LegendStatus.Active : LegendStatus.Disabled,
    [LegendType.Rent]: isRentEnable ? LegendStatus.Active : LegendStatus.Disabled,
    [LegendType.Sold]: isSoldEnable
      ? (!isBuyEnable && !isRentEnable ? LegendStatus.Active : LegendStatus.Inactive)
      : LegendStatus.Disabled,
  };
});

export type IAgentOfficeFeatureCollection = GeoJSON.FeatureCollection<GeoJSON.Point, IAgentOfficeFeatureProps>;

export const makeAgentOfficeBulletinsGeoDataSelector = (state: State, id?: PoiId) => createSelector([
  agentOfficeBulletinsDictionarySelector,
  () => id,
], (grouped, activeId): IAgentOfficeFeatureCollection => {
  const features = values(grouped).map((office) => {
    const { locationPoint } = office;

    if (!locationPoint || !locationPoint.lng || !locationPoint.lat) {
      return null;
    }
    const properties = makeAgentOfficeListingProperties(office, activeId === office.id);

    return makeFeature({
      lat: locationPoint.lat,
      lng: locationPoint.lng,
    }, properties);
  }, []).filter(Boolean);

  return {
    type: 'FeatureCollection',
    features: features.reverse(),
  };
});
